import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import { changeRequestsApi, CR_TYPE_META, CR_STATUS_META } from "@/api/changeRequests";
import { fmtNum } from "@/utils/toEnglishDigits";
import { fmtDate, fmtTime } from "@/utils/date";
import { formatApiError } from "@/lib/api";
import {
  CheckCircle,
  XCircle,
  Clock,
  Buildings,
  Note,
  ArrowsClockwise,
  User as UserIcon,
  ClipboardText,
} from "@phosphor-icons/react";

const TABS = [
  { key: "pending",  label: "قيد الانتظار" },
  { key: "approved", label: "معتمدة" },
  { key: "rejected", label: "مرفوضة" },
];

export default function AdminChangeRequests() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decideOn, setDecideOn] = useState(null); // {req, kind:'approve'|'reject'}

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const data = await changeRequestsApi.list(status, 200);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  return (
    <div className="min-h-screen bg-background text-foreground pb-16" dir="rtl">
      <TopBar />
      <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white inline-flex items-center gap-2">
              <ClipboardText size={26} weight="duotone" className="text-primary" />
              طلبات الموافقة
            </h1>
            <p className="text-white/50 text-sm mt-1">
              جميع تغييرات الشركات تُعرَض هنا للموافقة قبل التطبيق —{" "}
              <span className="text-primary font-bold num" data-testid="cr-total">
                {fmtNum(items.length)}
              </span>{" "}
              طلبات في هذا التبويب.
            </p>
          </div>
          <button
            type="button"
            onClick={() => load(tab)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 text-xs font-bold disabled:opacity-50"
            data-testid="cr-refresh"
          >
            <ArrowsClockwise size={13} weight="bold" /> تحديث
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-primary/15 pb-2 flex-wrap" data-testid="cr-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`cr-tab-${t.key}`}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-all border ${
                  active
                    ? "bg-primary text-primary-foreground border-primary shadow-[0_4px_12px_rgba(201,169,97,0.35)]"
                    : "bg-white/5 border-white/15 text-white/60 hover:bg-white/10"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : items.length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          <div className="space-y-3" data-testid="cr-list">
            {items.map((cr) => (
              <RequestCard
                key={cr.id}
                cr={cr}
                onApprove={() => setDecideOn({ req: cr, kind: "approve" })}
                onReject={() => setDecideOn({ req: cr, kind: "reject" })}
              />
            ))}
          </div>
        )}
      </main>

      {decideOn && (
        <DecideModal
          request={decideOn.req}
          kind={decideOn.kind}
          onClose={() => setDecideOn(null)}
          onDone={() => { setDecideOn(null); load(tab); }}
        />
      )}
    </div>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────
function EmptyState({ tab }) {
  const meta = {
    pending:  { icon: <Clock size={42} weight="duotone" />,       msg: "لا توجد طلبات قيد الانتظار حالياً." },
    approved: { icon: <CheckCircle size={42} weight="duotone" />, msg: "لم يتم اعتماد أي طلب بعد." },
    rejected: { icon: <XCircle size={42} weight="duotone" />,     msg: "لا توجد طلبات مرفوضة." },
  }[tab];
  return (
    <div className="rounded-2xl bg-card border border-primary/15 p-10 text-center" data-testid="cr-empty">
      <div className="text-primary/70 mb-3 flex justify-center">{meta.icon}</div>
      <div className="text-white/70">{meta.msg}</div>
    </div>
  );
}

// ─── Request card ──────────────────────────────────────────────────────
function RequestCard({ cr, onApprove, onReject }) {
  const key = `${cr.target_type}:${cr.action}`;
  const meta = CR_TYPE_META[key] || { label: key, color: "#C9A961", bg: "rgba(201,169,97,0.15)", border: "rgba(201,169,97,0.4)" };
  const status = CR_STATUS_META[cr.status] || CR_STATUS_META.pending;
  const iso = cr.created_at;

  return (
    <div
      className="rounded-2xl bg-card border border-primary/15 p-4 md:p-5 hover:border-primary/40 transition-all fadeup"
      data-testid={`cr-card-${cr.id}`}
    >
      {/* Top row */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-[11px] font-bold px-2.5 py-1 rounded-full border"
            style={{ background: meta.bg, borderColor: meta.border, color: meta.color }}
          >
            {meta.label}
          </span>
          <span
            className="text-[11px] font-bold px-2.5 py-1 rounded-full"
            style={{ background: `${status.color}15`, color: status.color, border: `1px solid ${status.color}50` }}
          >
            {status.label}
          </span>
          {iso && (
            <span className="text-white/45 text-[11px] num inline-flex items-center gap-1" dir="ltr">
              <Clock size={11} /> {fmtDate(iso)} · {fmtTime(iso)}
            </span>
          )}
        </div>
        {cr.status === "pending" && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onReject}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-destructive/15 border border-destructive/50 text-destructive hover:bg-destructive/25 text-xs font-bold"
              data-testid={`cr-reject-${cr.id}`}
            >
              <XCircle size={12} weight="bold" /> رفض
            </button>
            <button
              type="button"
              onClick={onApprove}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#22C55E]/15 border border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/25 text-xs font-bold"
              data-testid={`cr-approve-${cr.id}`}
            >
              <CheckCircle size={12} weight="bold" /> موافقة
            </button>
          </div>
        )}
      </div>

      {/* Company + summary */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/25 text-primary flex items-center justify-center shrink-0">
          <Buildings size={18} weight="duotone" />
        </div>
        <div className="min-w-0">
          <div className="text-white font-bold text-sm truncate" data-testid={`cr-company-${cr.id}`}>
            {cr.requester_company_name || cr.requester_email || "شركة"}
          </div>
          {cr.summary && (
            <div className="text-white/70 text-xs mt-0.5">{cr.summary}</div>
          )}
        </div>
      </div>

      {/* Data box */}
      <DataDiff cr={cr} />

      {/* Resolved note */}
      {cr.status !== "pending" && (
        <div className="mt-3 pt-3 border-t border-primary/10 text-xs text-white/60 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1">
            <UserIcon size={12} weight="duotone" className="text-primary/60" />
            {cr.resolved_by || "—"}
          </span>
          {cr.resolved_at && (
            <span className="num" dir="ltr">· {fmtDate(cr.resolved_at)} {fmtTime(cr.resolved_at)}</span>
          )}
          {cr.admin_note && (
            <span className="inline-flex items-center gap-1 ml-auto text-primary">
              <Note size={12} weight="duotone" /> {cr.admin_note}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Data diff (before/after) ──────────────────────────────────────────
function DataDiff({ cr }) {
  const { action, original_data: before, new_data: after } = cr;

  const renderRows = (obj, tone = "default") => {
    if (!obj) return <div className="text-white/40 text-xs">—</div>;
    const rows = Object.entries(obj)
      .filter(([k, v]) => v !== null && v !== undefined && v !== "" && !["phone1", "phone2", "created_at", "updated_at", "id"].includes(k))
      .slice(0, 20);
    return (
      <div className="text-xs space-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <span className="text-white/45 shrink-0 min-w-[100px]">{LABEL[k] || k}:</span>
            <span
              className={
                tone === "danger" ? "text-destructive line-through" : tone === "add" ? "text-[#22C55E]" : "text-white/85"
              }
              style={{ wordBreak: "break-word" }}
            >
              {formatVal(v)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (action === "create") {
    return (
      <div className="rounded-lg border border-[#22C55E]/25 bg-[#22C55E]/5 p-3">
        <div className="text-[10px] font-bold text-[#22C55E] mb-2 inline-flex items-center gap-1">
          <CheckCircle size={11} weight="fill" /> بيانات جديدة
        </div>
        {renderRows(after, "add")}
      </div>
    );
  }

  if (action === "delete") {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
        <div className="text-[10px] font-bold text-destructive mb-2 inline-flex items-center gap-1">
          <XCircle size={11} weight="fill" /> سيتم حذف
        </div>
        {renderRows(before, "danger")}
      </div>
    );
  }

  // update — show diff of changed fields only
  const changed = [];
  const beforeObj = before || {};
  const afterObj = after || {};
  const keys = new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)]);
  keys.forEach((k) => {
    if (["phone1", "phone2", "created_at", "updated_at", "id"].includes(k)) return;
    const b = beforeObj[k];
    const a = afterObj[k];
    if (JSON.stringify(b || "") !== JSON.stringify(a || "")) changed.push({ k, b, a });
  });
  if (changed.length === 0) {
    return <div className="text-white/45 text-xs">لا توجد تغييرات مكتشفة.</div>;
  }
  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
      <div className="text-[10px] font-bold text-primary mb-2">التغييرات</div>
      <div className="space-y-2 text-xs">
        {changed.map(({ k, b, a }) => (
          <div key={k} className="flex flex-col gap-0.5">
            <span className="text-white/45 text-[10px]">{LABEL[k] || k}</span>
            <div className="flex items-center gap-2 flex-wrap" dir="rtl">
              <span className="text-destructive/85 line-through" style={{ wordBreak: "break-word" }}>{formatVal(b) || "—"}</span>
              <span className="text-white/45">←</span>
              <span className="text-[#22C55E]" style={{ wordBreak: "break-word" }}>{formatVal(a) || "—"}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const LABEL = {
  name: "الاسم",
  email: "البريد",
  phones: "الأرقام",
  notes: "ملاحظات",
  client_name: "اسم العميل",
  client_phone: "جوال العميل",
  client_phone_alt: "جوال إضافي",
  pickup_location: "نقطة الانطلاق",
  destination: "الوجهة",
  dropoff_location: "الوجهة",
  trip_date: "التاريخ",
  trip_time: "الوقت",
  car_type: "نوع السيارة",
  car_type_name: "السيارة",
  car_name_ar: "السيارة",
  passengers: "الركاب",
  price: "السعر",
  total_price: "الإجمالي",
  base_price: "السعر الأساس",
  flight_number: "رقم الرحلة",
  flight_airline: "شركة الطيران",
  company_name: "الشركة",
  customer_email: "بريد العميل",
};

function formatVal(v) {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.join(" · ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// ─── Decide modal ──────────────────────────────────────────────────────
function DecideModal({ request, kind, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const isApprove = kind === "approve";

  const submit = async () => {
    setBusy(true);
    try {
      if (isApprove) {
        await changeRequestsApi.approve(request.id, note.trim() || undefined);
        toast.success("تم اعتماد الطلب ✓");
      } else {
        await changeRequestsApi.reject(request.id, note.trim() || undefined);
        toast.success("تم رفض الطلب");
      }
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      dir="rtl"
      data-testid="cr-decide-modal"
    >
      <div
        className="bg-card border border-primary/30 rounded-2xl w-full max-w-md gold-glow"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-primary/15 flex items-center justify-between">
          <div className={`inline-flex items-center gap-2 font-bold text-base ${isApprove ? "text-[#22C55E]" : "text-destructive"}`}>
            {isApprove ? <CheckCircle size={18} weight="fill" /> : <XCircle size={18} weight="fill" />}
            {isApprove ? "اعتماد الطلب" : "رفض الطلب"}
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white" data-testid="cr-decide-close">
            <XCircle size={16} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="rounded-lg bg-black/30 border border-primary/15 p-3 text-xs text-white/80">
            {request.summary || "—"}
          </div>
          <label className="block">
            <div className="text-xs text-white/60 mb-1.5 text-right">ملاحظة (اختياري)</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={isApprove ? "مثلاً: تم التحقق من البيانات" : "مثلاً: البيانات غير كافية"}
              className="mz-input resize-none"
              data-testid="cr-decide-note"
            />
          </label>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className={`flex-1 py-2.5 rounded-xl font-bold text-sm text-white disabled:opacity-50 inline-flex items-center justify-center gap-1.5 ${
                isApprove ? "bg-[#22C55E] hover:bg-[#16A34A]" : "bg-destructive hover:bg-destructive/85"
              }`}
              data-testid="cr-decide-confirm"
            >
              {isApprove ? <CheckCircle size={14} weight="bold" /> : <XCircle size={14} weight="bold" />}
              {busy ? "جارٍ الحفظ…" : isApprove ? "تأكيد الاعتماد" : "تأكيد الرفض"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-5 py-2.5 rounded-xl border border-white/20 text-white/70 text-sm hover:bg-white/5"
            >
              إلغاء
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
